import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	input,
	OnInit,
	signal,
} from '@angular/core';
import { CourseService } from '../../services/course.service';
import { Store } from '@ngrx/store';
import { ClientValidatorService } from '@uc/web/core';
import { ClientFeatures, Gallery } from '@uc/web/shared/data-models';
import {
	fetchClearingUniversity,
	fetchClearingCourse,
} from '../../data-access/+state/course.actions';
import {
	getStateClearingCourse,
	getStateClearingUniversity,
	getStateCourseIsLoading,
	getStateUniIsLoading,
} from '../../data-access/+state/course.selectors';
import {
	CtaClearingComponent,
	UNI_SLIDER_COPY,
	UniversitySliderComponent,
} from '@uc/web/shared/features/feature-university-slider';
import { ClearingService, RemoveDuplicateDomElementDirective } from '@uc/utilities';
import { CourseAnalyticsService } from '../../services/course-analytics.service';
import { HeaderTitleComponent } from '../../components/header-title/header-title.component';
import { ClearingCourseHeadingsComponent } from '../../components/clearing-course-headings/clearing-course-headings.component';
import { CourseInformationComponent } from '../../components/course-information/course-information.component';
import { CourseProfileApplicationDetailsComponent } from '../../components/course-profile-application-details/course-profile-application-details.component';
import { EntryRequirementsComponent } from '../../components/entry-requirements/entry-requirements.component';
import { FeesAndFundingComponent } from '../../components/fees-and-funding/fees-and-funding.component';
import { NoFeesAndFundingComponent } from '../../components/no-fees-and-funding/no-fees-and-funding.component';
import { CourseSkeletonComponent } from '../../components/course-skeleton/course-skeleton.component';
import {
	ClearingAvailableBadgeComponent,
	ClearingCtaFinalComponent,
	CourseSearchBannerComponent,
	EngageCardCtaCallUniComponent,
	EngageCardRegisterInterestCtaComponent,
	UniversityHeaderComponent,
} from '@web/shared/ui';
import {
	ClientProfileStickyLeadBarDesktopComponent,
	ClientProfileStickyLeadBarMobileComponent,
} from '@web/shared/features/feature-sticky-lead-bar';
import {
	RegisterInterestBtnComponent,
	CallUniBtnComponent,
} from '@uc/web/shared/buttons';
import { ClearingVisitWebsiteBtnComponent } from '@uc/web/shared/features/feature-clearing-visit-website';
import { NA } from '@uc/web/analytics';
import { Product, ProductEnum } from '@uc/shared/data-models';

@Component({
	selector: 'uc-clearing-course-details',
	templateUrl: './clearing-course-details.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		RemoveDuplicateDomElementDirective,
		HeaderTitleComponent,
		UniversityHeaderComponent,
		ClearingAvailableBadgeComponent,
		ClearingCourseHeadingsComponent,
		CourseInformationComponent,
		RegisterInterestBtnComponent,
		CallUniBtnComponent,
		CourseProfileApplicationDetailsComponent,
		EntryRequirementsComponent,
		ClearingVisitWebsiteBtnComponent,
		CourseSearchBannerComponent,
		FeesAndFundingComponent,
		NoFeesAndFundingComponent,
		EngageCardRegisterInterestCtaComponent,
		ClearingCtaFinalComponent,
		ClientProfileStickyLeadBarDesktopComponent,
		CourseSkeletonComponent,
		UniversitySliderComponent,
		CtaClearingComponent,
		ClientProfileStickyLeadBarMobileComponent,
		EngageCardCtaCallUniComponent,
	],
})
export class ClearingCourseDetailsComponent implements OnInit {
	uniSlug = input.required<string>();
	courseSlug = input.required<string>();
	preview = input<boolean>();

	selectedIndex = signal<number>(0);

	isFeaturedClearing = computed(() => {
		const paidFeatures = this.university()?.paid_features;
		return this._cvSrv.checkFeature(paidFeatures, this.paidFeatures);
	});

	isFeaturedRegisterInterest = computed(() => {
		const paidFeatures = this.university()?.paid_features;
		return this._cvSrv.checkFeature(paidFeatures, ClientFeatures.RegisterInterest);
	});

	isFeaturedClearingVisitWebsite = computed(() => {
		const paidFeatures = this.university()?.paid_features;
		return this._cvSrv.checkFeature(
			paidFeatures,
			ClientFeatures.ClearingVisitWebsite,
		);
	});

	isFeaturedCalls = computed(() => {
		const paidFeatures = this.university()?.paid_features;
		return this._cvSrv.checkFeature(paidFeatures, ClientFeatures.Calls);
	});

	uniGallery = computed(() => {
		const university = this.university();
		if (!university) return;

		const gallery: Gallery = {
			cover_image: university.cover_image,
			images: university.gallery,
		};
		return gallery;
	});

	externalUrl = computed(() => {
		const course = this.course();
		const uni = this.university();

		if (!uni || !course) return '';
		return course.university.external_url ?? uni.external_url;
	});

	university = this._store.selectSignal(getStateClearingUniversity);

	course = this._store.selectSignal(getStateClearingCourse);

	isCourseLoading = this._store.selectSignal(getStateCourseIsLoading);
	isUniLoading = this._store.selectSignal(getStateUniIsLoading);
	isLoading = computed(() => this.isCourseLoading() || this.isUniLoading());

	readonly paidFeatures = [ClientFeatures.Clearing];
	currentClearingYear = this._clearingSrv.getYear();
	uniSliderCopy = { ...UNI_SLIDER_COPY.course.clearing };

	product: Product = ProductEnum.Clearing;
	clearingCourseEnabled = true;

	constructor(
		private _store: Store,
		private _courseAnalyticsSrv: CourseAnalyticsService,
		private _clearingSrv: ClearingService,
		private _courseSrv: CourseService,
		private _cvSrv: ClientValidatorService,
	) {
		effect(() => {
			const course = this.course();
			const uni = this.university();
			if (!uni || !course) return;

			this._courseSrv.updateSeoMetadata(course.name, course.university.name);

			this._courseAnalyticsSrv.executeGAEvents(
				uni.id,
				uni.name,
				uni.order,
				course.id,
				course.name,
				this.product,
				course.subjects[0] || NA,
				NA,
				'Clearing',
				uni.address?.region || NA,
			);

			if (this.preview()) {
				// Display the preview view only if the preview query param is present in the url
				this.paidFeatures.push(ClientFeatures.ClearingPreview);
			}
		});
	}

	ngOnInit(): void {
		const uniSlug = this.uniSlug();
		const courseSlug = this.courseSlug();

		this._store.dispatch(fetchClearingUniversity({ uniSlug }));
		this._store.dispatch(fetchClearingCourse({ courseSlug }));
	}

	onCourseOptionSelect(index: number) {
		this.selectedIndex.set(index);
	}
}
